import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AfipVoucher, ServiceBooking } from '@turizi/shared';
import {
    Button,
    TextInput,
    NumberInput,
    Select,
    SegmentedControl,
    ActionIcon,
    Tooltip,
    FileInput,
    Loader,
    Checkbox,
} from '@mantine/core';
import CardModal from '@/components/card-modal';
import {
    IconReceipt,
    IconPlus,
    IconTrash,
    IconExternalLink,
    IconUpload,
    IconLink,
} from '@tabler/icons-react';
import { formatPrice, formatDayShort } from '@/lib/format-utils';
import { MoneyNumberInput } from '@/components/snippets';
import CurrencySelector from '@/components/currency-selector';
import {
    useCreateReceivedAfipVoucher,
    useGetAfipVouchers,
    useLinkVoucherToServiceBooking,
    useUnlinkVoucherFromServiceBooking,
    useUnpaidServiceBookings,
} from '@/hooks/api-hooks';
import api from '@/services/api-service';
import { notify } from '@/lib/notify';

interface BookingProviderVouchersSectionProps {
    booking: ServiceBooking;
}

const VOUCHER_TYPE_OPTIONS = [
    { value: '1', label: 'Factura A' },
    { value: '6', label: 'Factura B' },
    { value: '11', label: 'Factura C' },
    { value: '2', label: 'Nota de Débito A' },
    { value: '3', label: 'Nota de Crédito A' },
    { value: '7', label: 'Nota de Débito B' },
    { value: '8', label: 'Nota de Crédito B' },
    { value: '12', label: 'Nota de Débito C' },
    { value: '13', label: 'Nota de Crédito C' },
];

export default function BookingProviderVouchersSection({
    booking,
}: BookingProviderVouchersSectionProps) {
    const { t } = useTranslation();
    const [modalOpened, setModalOpened] = useState(false);
    const [mode, setMode] = useState<'new' | 'existing'>('new');

    const linkedVouchers: AfipVoucher[] = Array.isArray(booking.afip_vouchers)
        ? booking.afip_vouchers
        : [];
    const linkedIds = linkedVouchers.map((v) => v.id);

    const providerId = booking.selected_provider_id || booking.selected_provider?.id;
    const providerName = booking.selected_provider?.name || '';
    const providerCuit = booking.selected_provider?.tax_id_number || '';

    const createReceivedMutation = useCreateReceivedAfipVoucher();
    const linkMutation = useLinkVoucherToServiceBooking();
    const unlinkMutation = useUnlinkVoucherFromServiceBooking();

    const { data: receivedVouchers } = useGetAfipVouchers(
        {
            direction: 'received',
            issuer_business_id: providerId ? Number(providerId) : undefined,
        },
        { enabled: modalOpened && mode === 'existing' }
    );

    const { data: providerBookingsRes } = useUnpaidServiceBookings(
        providerId ? Number(providerId) : undefined,
        { enabled: modalOpened && mode === 'new' && !!providerId }
    );

    const otherProviderBookings = (providerBookingsRes?.data || []).filter(
        (b) => Number(b.id) !== Number(booking.id)
    );

    // Form state for new received voucher
    const [cbteTipo, setCbteTipo] = useState('1');
    const [pos, setPos] = useState<number | string>(1);
    const [voucherNumber, setVoucherNumber] = useState<number | string>('');
    const [issueDate, setIssueDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
    const [currency, setCurrency] = useState<string>(booking.money_out_currency || 'ARS');
    const [impTotal, setImpTotal] = useState<number>(Number(booking.money_out_amount || 0));
    const [cae, setCae] = useState('');
    const [caeExpiration, setCaeExpiration] = useState('');
    const [issuerCuit, setIssuerCuit] = useState(providerCuit);
    const [attachments, setAttachments] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [extraBookingIds, setExtraBookingIds] = useState<number[]>([]);

    // Form state for existing voucher link
    const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null);

    useEffect(() => {
        if (modalOpened) {
            setImpTotal(Number(booking.money_out_amount || 0));
            setCurrency(booking.money_out_currency || 'ARS');
            setIssuerCuit(providerCuit);
            setExtraBookingIds([]);
            setSelectedVoucherId(null);
        }
    }, [modalOpened, booking.money_out_amount, booking.money_out_currency, providerCuit]);

    const handleUploadFile = async (file: File | null) => {
        if (!file) return;
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await api.upload<{ url: string; key: string }>('/payments/upload-attachment', formData);
            if (res.error) {
                notify.error(t('error'), typeof res.error === 'string' ? res.error : t('error'));
            } else if (res.data?.url) {
                setAttachments((prev) => [...prev, res.data!.url]);
            }
        } catch {
            notify.error(t('error'), t('error'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleCreateAndLink = () => {
        if (!booking.id || !pos || !voucherNumber || impTotal <= 0) return;

        const allBookingIds = [Number(booking.id), ...extraBookingIds];

        createReceivedMutation.mutate(
            {
                issuer_business_id: providerId ? Number(providerId) : null,
                issuer_name: providerName,
                issuer_cuit: issuerCuit,
                issue_date: issueDate || null,
                currency,
                attachments,
                pos: Number(pos),
                cbte_tipo: Number(cbteTipo),
                voucher_number: Number(voucherNumber),
                imp_total: impTotal,
                imp_neto: impTotal,
                imp_iva: 0,
                cae: cae.trim(),
                cae_expiration: caeExpiration.trim(),
                service_booking_ids: allBookingIds,
            },
            {
                onSuccess: () => {
                    notify.success(t('provider_voucher_created'), t('success'));
                    setModalOpened(false);
                    setVoucherNumber('');
                    setCae('');
                    setCaeExpiration('');
                    setAttachments([]);
                },
            }
        );
    };

    const handleLinkExisting = () => {
        if (!booking.id || !selectedVoucherId) return;
        linkMutation.mutate(
            {
                serviceBookingId: booking.id,
                voucherId: Number(selectedVoucherId),
            },
            {
                onSuccess: () => {
                    notify.success(t('provider_voucher_linked'), t('success'));
                    setModalOpened(false);
                },
            }
        );
    };

    const handleUnlink = (voucherId: number) => {
        if (!booking.id) return;
        unlinkMutation.mutate(
            {
                serviceBookingId: booking.id,
                voucherId,
            },
            {
                onSuccess: () => {
                    notify.success(t('provider_voucher_unlinked'), t('success'));
                },
            }
        );
    };

    const availableExistingVouchers = (receivedVouchers || []).filter(
        (v) => !linkedIds.includes(v.id)
    );

    return (
        <div className="solid-box-border-tinted space-y-2 mt-2">
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <IconReceipt size={13} className="text-slate-500" />
                    {t('provider_tax_vouchers')}
                </span>
                <Button
                    size="compact-xs"
                    variant="light"
                    color="dark"
                    leftSection={<IconPlus size={12} />}
                    onClick={() => setModalOpened(true)}
                >
                    {t('add')}
                </Button>
            </div>

            {linkedVouchers.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic">
                    {t('no_provider_vouchers')}
                </div>
            ) : (
                <div className="space-y-1.5">
                    {linkedVouchers.map((voucher) => {
                        const formattedNumber = `${String(voucher.pos).padStart(4, '0')}-${String(voucher.voucher_number).padStart(8, '0')}`;
                        const firstAttachment = Array.isArray(voucher.attachments) && voucher.attachments.length > 0
                            ? voucher.attachments[0]
                            : null;
                        return (
                            <div
                                key={voucher.id}
                                className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200/80 text-xs"
                            >
                                <div className="min-w-0 pr-2">
                                    <div className="flex items-center gap-1.5 font-mono font-bold text-slate-800">
                                        <span>{formattedNumber}</span>
                                        {voucher.issuer_name && (
                                            <span className="font-sans font-normal text-[10px] text-slate-500 truncate">
                                                ({voucher.issuer_name})
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                        ${formatPrice(Number(voucher.imp_total || 0))}{' '}
                                        <span className="text-[10px] font-bold uppercase">
                                            {voucher.currency || 'ARS'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {firstAttachment && (
                                        <Tooltip label={t('attachments')} withArrow>
                                            <ActionIcon
                                                component="a"
                                                href={firstAttachment}
                                                target="_blank"
                                                rel="noreferrer"
                                                size="sm"
                                                variant="light"
                                                color="blue"
                                            >
                                                <IconExternalLink size={14} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                    <Tooltip label={t('delete_link')} withArrow>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="red"
                                            loading={unlinkMutation.isPending}
                                            onClick={() => handleUnlink(voucher.id)}
                                        >
                                            <IconTrash size={14} />
                                        </ActionIcon>
                                    </Tooltip>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <CardModal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                header={
                    <div className="flex items-center gap-2 font-bold text-sm text-slate-800">
                        <IconReceipt size={16} className="text-blue-600" />
                        <span>{t('add_provider_voucher')}</span>
                    </div>
                }
                size="full"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => setModalOpened(false)}
                        >
                            {t('cancel')}
                        </Button>
                        {mode === 'new' ? (
                            <Button
                                size="xs"
                                color="dark"
                                loading={createReceivedMutation.isPending}
                                // disabled={!pos || !voucherNumber || impTotal <= 0}
                                onClick={handleCreateAndLink}
                            >
                                {t('save')}
                            </Button>
                        ) : (
                            <Button
                                size="xs"
                                color="blue"
                                leftSection={<IconLink size={14} />}
                                loading={linkMutation.isPending}
                                // disabled={!selectedVoucherId}
                                onClick={handleLinkExisting}
                            >
                                {t('link_existing_voucher')}
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="space-y-4">
                    <SegmentedControl
                        fullWidth
                        size="xs"
                        value={mode}
                        onChange={(val) => setMode(val as 'new' | 'existing')}
                        data={[
                            { value: 'new', label: t('register_new_voucher') },
                            { value: 'existing', label: t('link_existing_voucher') },
                        ]}
                    />

                    {mode === 'new' ? (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <Select
                                    size="xs"
                                    label={t('voucher_type_label')}
                                    data={VOUCHER_TYPE_OPTIONS}
                                    value={cbteTipo}
                                    onChange={(val) => setCbteTipo(val || '1')}
                                    allowDeselect={false}
                                />
                                <NumberInput
                                    size="xs"
                                    label={t('pos_label')}
                                    value={pos}
                                    onChange={(val) => setPos(val === '' ? '' : Number(val))}
                                    min={1}
                                    hideControls
                                />
                                <NumberInput
                                    size="xs"
                                    label={t('voucher_number_label')}
                                    value={voucherNumber}
                                    onChange={(val) => setVoucherNumber(val === '' ? '' : Number(val))}
                                    min={1}
                                    hideControls
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <TextInput
                                    size="xs"
                                    label={t('issuer_cuit')}
                                    value={issuerCuit}
                                    onChange={(e) => setIssuerCuit(e.currentTarget.value)}
                                />
                                <TextInput
                                    size="xs"
                                    type="date"
                                    label={t('issue_date')}
                                    value={issueDate}
                                    onChange={(e) => setIssueDate(e.currentTarget.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <CurrencySelector
                                    label={t('currency')}
                                    value={currency}
                                    onChange={(val) => setCurrency(val || 'ARS')}
                                />
                                <MoneyNumberInput
                                    label={t('amount')}
                                    amount={impTotal}
                                    onChange={(val) => setImpTotal(Number(val || 0))}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <TextInput
                                    size="xs"
                                    label={t('cae_optional')}
                                    value={cae}
                                    onChange={(e) => setCae(e.currentTarget.value)}
                                />
                                <TextInput
                                    size="xs"
                                    type="date"
                                    label={t('cae_expiration_optional')}
                                    value={caeExpiration}
                                    onChange={(e) => setCaeExpiration(e.currentTarget.value)}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <FileInput
                                    size="xs"
                                    label={t('upload_voucher_file')}
                                    placeholder={t('upload')}
                                    accept="image/png,image/jpeg,application/pdf"
                                    leftSection={<IconUpload size={14} />}
                                    onChange={handleUploadFile}
                                    disabled={isUploading}
                                />
                                {isUploading && <Loader size="xs" />}
                                {attachments.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {attachments.map((url, idx) => (
                                            <a
                                                key={idx}
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200"
                                            >
                                                <IconExternalLink size={12} />
                                                <span>#{idx + 1}</span>
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {otherProviderBookings.length > 0 && (
                                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1.5 max-h-40 overflow-y-auto">
                                    <div className="text-[11px] font-bold text-slate-600">
                                        {t('link_to_other_bookings_of_provider')}
                                    </div>
                                    {otherProviderBookings.map((otherBk) => {
                                        const bkId = Number(otherBk.id);
                                        const checked = extraBookingIds.includes(bkId);
                                        return (
                                            <Checkbox
                                                key={bkId}
                                                size="xs"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const isChecked = e.currentTarget.checked;
                                                    setExtraBookingIds((prev) =>
                                                        isChecked
                                                            ? [...prev, bkId]
                                                            : prev.filter((id) => id !== bkId)
                                                    );
                                                }}
                                                label={`#${bkId} - ${otherBk.service?.name || otherBk.service_data?.name || ''} (${formatDayShort(otherBk.start_day)}) - $${formatPrice(Number(otherBk.money_out_amount || 0))} ${otherBk.money_out_currency || ''}`}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {availableExistingVouchers.length === 0 ? (
                                <div className="text-xs text-slate-400 italic py-4 text-center">
                                    {t('no_received_vouchers_for_provider')}
                                </div>
                            ) : (
                                <Select
                                    size="xs"
                                    label={t('select_existing_voucher')}
                                    data={availableExistingVouchers.map((v) => ({
                                        value: String(v.id),
                                        label: `${String(v.pos).padStart(4, '0')}-${String(v.voucher_number).padStart(8, '0')} | $${formatPrice(Number(v.imp_total || 0))} ${v.currency || 'ARS'} (${v.issuer_name || ''})`,
                                    }))}
                                    value={selectedVoucherId}
                                    onChange={(val) => setSelectedVoucherId(val ? String(val) : '')}
                                    searchable
                                />
                            )}
                        </div>
                    )}
                </div>
            </CardModal>
        </div>
    );
}
